<template>
  <div :class="{ editable, disabled, full }" class="sf-calculator">
    <div v-if="mode !== 'full'" class="edit" @click="toggleEditable">
      <slot name="edit-loan"/>
      <pencil :class="{ active: editable }" class="pencil"/>
    </div>

    <template v-if="!hideSelectors">
      <field ref="amount" v-model="loanValue" :settings="loanSettings" :editable="editable">
        <slot slot="label" name="loan-amount"/>
        <template slot="value" slot-scope="{ value }">
          <b>{{ value | bigNumber }}</b> {{ $tools.currency.local }}
        </template>
      </field>

      <field
        v-if="showTerm && showTermStl"
        ref="term"
        v-model="timeValue"
        :settings="timeSettings"
        :editable="editable"
        @mousedown="$emit('mousedown')">
        <slot slot="label" name="loan-days"/>
        <template slot="value" slot-scope="{ value }">
          <template v-if="showDaysStl || editable">
            <slot name="term-select" :value="value">
              <b>{{ value }}</b> <slot name="days-select"/>
            </slot>
          </template>
        </template>
      </field>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import Pencil from '../img/icons/pencil.svg';
import Field from './partials/calculator-field';

export default {
  components: { Field, Pencil },
  props: {
    showTermStl: {
      type: Boolean,
      default: true,
    },
    showDaysStl: {
      // days are always shown in edit mode
      type: Boolean,
      default: true,
    },
    mode: {
      type: String,
      default: 'full', // full || edit
    },
    limit: {
      type: String,
      default: '',
    },
    colors: {
      type: Object,
      default() {
        return {
          process: this.$theme.colors.secondary,
          arrows: this.$theme.colors.secondary,
          dot: '#fff',

          processAlt: '#fff',
          arrowsAlt: '#fff',
          dotAlt: this.$theme.colors.secondary,
        };
      },
    },
    override: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      editable: this.mode === 'full',
      size: 0,
      halfOfDotSize: 18,
      minAmountSize: 0,
      maxAmountSize: 0,
      processSize: 0,
      hideSelectors: false,
    };
  },
  computed: {
    ...mapState('arithmetic', ['type']),
    ...mapGetters('arithmetic', [
      'authorized',
      'amount',
      'term',
      'additional',
      'displayed',
      'repeated',
      'disabled',
      'offerKey',
      'useTermFromApplicationOnIncrease',
    ]),
    ...mapGetters('client', ['application']),

    max() {
      return this.limit === 'first' && !this.authorized ? this.amount.first : this.amount.max;
    },

    loanValue: {
      get() {
        return this.amount.current;
      },

      set(value) {
        this.setValue({ key: 'amount', value });
        if (value > this.amount.first) {
          this.setValue({ key: 'tab', value: 1 });
        } else if (this.type === 'loc') {
          this.setValue({ key: 'tab', value: 0 });
        }
      },
    },

    timeValue: {
      get() {
        return this.term.current;
      },

      set(value) {
        this.setValue({ key: 'term', value });
      },
    },

    loanSettings() {
      const styledProcessStyle = {};
      let styledSliderStyle = {};
      const minValue = this.override.min || this.amount.min;

      const minAmount = minValue === this.amount.max ? 0 : minValue;
      const breakpoint = this.authorized ? this.amount.max : this.amount.first;
      const breakpointPx = `${(breakpoint / this.max) * this.size}px`;
      styledProcessStyle.processStyle = {
        background: `linear-gradient(to right,
          ${this.colors.process} ${breakpointPx},
          ${this.colors.processAlt} ${breakpointPx})`,
      };

      if (this.loanValue > this.amount.first) {
        styledSliderStyle = {
          sliderStyle: {
            backgroundColor: this.colors.dotAlt,
            color: this.colors.arrowsAlt,
          },
        };
      } else {
        styledSliderStyle = {
          sliderStyle: {
            backgroundColor: this.colors.dot,
            color: this.colors.arrows,
          },
        };
      }

      return {
        ...styledProcessStyle,
        ...styledSliderStyle,
        ...this.amount,
        min: minAmount,
        max: this.max,
      };
    },

    full() {
      return this.mode === 'full';
    },

    timeSettings() {
      return {
        ...this.term,
        sliderStyle: {
          backgroundColor: this.colors.dot,
          color: this.colors.arrows,
        },
        processStyle: {
          background: this.colors.process,
        },
      };
    },

    showTerm() {
      const isStlAndAdditional = this.type === 'stl' && !this.additional;

      if (!this.application) return isStlAndAdditional;

      const isOfferIncrease =
        this.application.status === this.$tools.status.APPLICATION_YHAT_INCREASE;

      if (this.useTermFromApplicationOnIncrease) return isStlAndAdditional && !isOfferIncrease;

      return isStlAndAdditional;
    },
  },
  async mounted() {
    await this.$nextTick();
    this.size = this.$refs.amount.size;
    this.$watch('$refs.amount.size', val => {
      this.size = val;
    });

    if (this.$nuxt && this.$nuxt.$loading) {
      this.hideSelectors = this.$nuxt.$loading.loading;

      this.$nuxt.$loading.$on('start', () => (this.hideSelectors = true));
      this.$nuxt.$loading.$on('finish', () => (this.hideSelectors = false));
    }
  },
  methods: {
    ...mapMutations('arithmetic', ['setValue']),
    ...mapActions('arithmetic', ['getData', 'updateLimit']),

    refresh() {
      const { amount, term } = this.$refs;
      if (amount) amount.refresh();
      if (term) term.refresh();
    },
    async toggleEditable() {
      this.editable = !this.editable;
      await this.$nextTick();
      this.refresh();
      this.$emit('edit', this.editable);
    },
  },
};
</script>

<style lang="scss" scoped>
.sf-calculator {
  position: relative;
  width: 100%;
  outline: none !important;

  &.disabled {
    .value-label.amount {
      display: none !important;
    }

    .value-select.amount {
      display: none !important;
    }
  }
}

.edit {
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  .pencil {
    width: 20px;
    height: 20px;

    /deep/ path {
      transition: fill 0.3s;
      fill: map-get($map: $colors, $key: secondary);
    }

    &.active /deep/ path {
      fill: map-get($map: $colors, $key: placeholder);
    }
  }
}
</style>
